// src/services/PersianDate.ts

interface DayInfo {
  date: string;
  title: string;
  type: number;
  isHoliday: boolean;
}

class PersianDate {
  private _day: number = 1;
  private _month: number = 1;
  private _year: number = 1300;
  private _hour: number = 0;
  private _minute: number = 0;
  private _second: number = 0;
  private _errorMessage: string = "تاریخ نامشخص است";

  public static readonly WeekDayNames: string[] = ["شنبه", "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه"];
  public static readonly MonthNames: string[] = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
  ];
  public static readonly PersianDigits: string[] = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

  constructor();
  constructor(persianDate: PersianDate);
  constructor(gregorianDate: Date);
  constructor(secondsFromEpoch: number);
  constructor(year: number, month: number, day: number);
  constructor(year: number, month: number, day: number, hour: number, minute: number, second: number);
  constructor(dateString: string);
  constructor(...args: any[]) {
    if (args.length === 0) {
      this.setDefaults();
    } else if (args.length === 1 && args[0] instanceof PersianDate) {
      this.copyFromPersianDate(args[0]);
    } else if (args.length === 1 && args[0] instanceof Date) {
      this.setDefaults();
      this.gregorianDate = args[0];
    } else if (args.length === 1 && typeof args[0] === 'number') {
      this.setDefaults();
      const epochDate = new Date(1970, 0, 1);
      epochDate.setSeconds(args[0]);
      this.gregorianDate = epochDate;
    } else if (args.length === 3) {
      this.setDefaults();
      this._year = args[0];
      this._month = args[1];
      this._day = args[2];
    } else if (args.length === 6) {
      this.setDefaults();
      this._year = args[0];
      this._month = args[1];
      this._day = args[2];
      this._hour = args[3];
      this._minute = args[4];
      this._second = args[5];
    } else if (args.length === 1 && typeof args[0] === 'string') {
      this.setDefaults();
      try {
        const dateString = this.toEnglishDigits(args[0].trim());
        const parts = dateString.split(' ');
        const dateParts = parts[0].split('/');
        this._year = parseInt(dateParts[0]);
        this._month = parseInt(dateParts[1]);
        this._day = parseInt(dateParts[2]);

        if (parts.length > 1) {
          const timeParts = parts[1].split(':');
          if (timeParts.length > 0) this._hour = parseInt(timeParts[0]);
          if (timeParts.length > 1) this._minute = parseInt(timeParts[1]);
          if (timeParts.length > 2) this._second = parseInt(timeParts[2]);
        }
      } catch {
        this._errorMessage = "فرمت تاریخ نادرست است.";
      }
      this._errorMessage = this.checkPersianDate();
    }
  }

  // Getters and Setters
  get day(): number { return this._day; }
  set day(value: number) { this._day = value; }
  get month(): number { return this._month; }
  set month(value: number) { this._month = value; }
  get year(): number { return this._year; }
  set year(value: number) { this._year = value; }
  get hour(): number { return this._hour; }
  set hour(value: number) { this._hour = value; }
  get minute(): number { return this._minute; }
  set minute(value: number) { this._minute = value; }
  get second(): number { return this._second; }
  set second(value: number) { this._second = value; }
  get errorMessage(): string { return this._errorMessage; }


  private static persianMonths = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
  ];

  // میلادی به شمسی
  static toPersian(date: Date): { year: number; month: number; day: number } {
    const gy = date.getFullYear();
    const gm = date.getMonth() + 1;
    const gd = date.getDate();

    let g_day_no =
      365 * (gy - 1600) +
      Math.floor((gy - 1600 + 3) / 4) -
      Math.floor((gy - 1600 + 99) / 100) +
      Math.floor((gy - 1600 + 399) / 400);

    for (let i = 0; i < gm - 1; ++i) {
      g_day_no += PersianDate.gDaysInMonth(i, gy);
    }
    g_day_no += gd - 1;

    let j_day_no = g_day_no - 79;

    let j_np = Math.floor(j_day_no / 12053);
    j_day_no %= 12053;

    let jy = 979 + 33 * j_np + 4 * Math.floor(j_day_no / 1461);
    j_day_no %= 1461;

    if (j_day_no >= 366) {
      jy += Math.floor((j_day_no - 1) / 365);
      j_day_no = (j_day_no - 1) % 365;
    }

    let jm, jd;
    for (jm = 0; jm < 11 && j_day_no >= PersianDate.jDaysInMonth(jm); ++jm) {
      j_day_no -= PersianDate.jDaysInMonth(jm);
    }
    jd = j_day_no + 1;

    return { year: jy, month: jm + 1, day: jd };
  }

  // شمسی به میلادی
  static toGregorian(jy: number, jm: number, jd: number): Date {
    jy -= 979;
    jm -= 1;
    jd -= 1;

    let j_day_no =
      365 * jy +
      Math.floor(jy / 33) * 8 +
      Math.floor(((jy % 33) + 3) / 4);

    for (let i = 0; i < jm; ++i) {
      j_day_no += PersianDate.jDaysInMonth(i);
    }

    j_day_no += jd;

    let g_day_no = j_day_no + 79;

    let gy = 1600 + 400 * Math.floor(g_day_no / 146097);
    g_day_no %= 146097;

    let leap = true;
    if (g_day_no >= 36525) {
      g_day_no--;
      gy += 100 * Math.floor(g_day_no / 36524);
      g_day_no %= 36524;

      if (g_day_no >= 365) {
        g_day_no++;
      } else {
        leap = false;
      }
    }

    gy += 4 * Math.floor(g_day_no / 1461);
    g_day_no %= 1461;

    if (g_day_no >= 366) {
      leap = false;
      g_day_no--;
      gy += Math.floor(g_day_no / 365);
      g_day_no %= 365;
    }

    let gm;
    for (gm = 0; g_day_no >= PersianDate.gDaysInMonth(gm, gy); gm++) {
      g_day_no -= PersianDate.gDaysInMonth(gm, gy);
    }
    const gd = g_day_no + 1;

    return new Date(gy, gm, gd);
  }

  private static gDaysInMonth(m: number, y: number): number {
    return [31, PersianDate.isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m];
  }

  private static jDaysInMonth(m: number): number {
    return [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 30, 29][m];
  }

  private static isLeap(y: number): boolean {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  }

  static formatPersian(date: Date): string {
    const p = PersianDate.toPersian(date);
    return `${p.year}/${p.month}/${p.day}`;
  }

  // Static Now
  public static get Now(): PersianDate {
    const now = new Date();
    return new PersianDate(now);
  }

  // Static Today
  public static get Today(): PersianDate {
    const tmpDate = PersianDate.Now;
    return new PersianDate(tmpDate.year, tmpDate.month, tmpDate.day);
  }

  // Static Tomorrow
  public static get Tomorrow(): PersianDate {
    const tmpDate = PersianDate.Now.addDays(1);
    return new PersianDate(tmpDate.year, tmpDate.month, tmpDate.day);
  }

  // Static Yesterday
  public static get Yesterday(): PersianDate {
    const tmpDate = PersianDate.Now.addDays(-1);
    return new PersianDate(tmpDate.year, tmpDate.month, tmpDate.day);
  }

  // IsToday
  get isToday(): boolean {
    return this.year === PersianDate.Today.year &&
      this.month === PersianDate.Today.month &&
      this.day === PersianDate.Today.day;
  }
  // WasYesterday
  get wasYesterday(): boolean {
    return this.year === PersianDate.Today.year &&
      this.month === PersianDate.Today.month &&
      this.day === PersianDate.Today.day - 1;
  }

  // SpecialEvents
  get specialEvents(): DayInfo {
    return PersianDayEvent.getInfo(this.year, this.month, this.day);
  }

  // FirstOfCurrentMonth
  get firstOfCurrentMonth(): PersianDate {
    const now = PersianDate.Now;
    return new PersianDate(now.year, now.month, 1);
  }

  // StartOfCurrentMonth
  get startOfCurrentMonth(): PersianDate {
    return this.firstOfCurrentMonth;
  }

  // EndOfCurrentMonth
  get endOfCurrentMonth(): PersianDate {
    const now = PersianDate.Now;
    return new PersianDate(now.year, now.month, PersianDate.getDaysInMonth(now.year, now.month));
  }

  // SecondsFromEpoch
  get secondsFromEpoch(): number {
    return Math.floor((this.gregorianDate.getTime() - new Date(1970, 0, 1).getTime()) / 1000);
  }

  // GregorianDate
  get gregorianDate(): Date {
    return this.jalaliToGregorian(this.year, this.month, this.day, this.hour, this.minute, this.second);
  }
  set gregorianDate(value: Date) {
    try {
      const jalali = this.gregorianToJalali(value.getFullYear(), value.getMonth() + 1, value.getDate());
      this._year = jalali.year;
      this._month = jalali.month;
      this._day = jalali.day;
      this._hour = value.getHours();
      this._minute = value.getMinutes();
      this._second = value.getSeconds();
      this._errorMessage = "";
    } catch {
      this._errorMessage = "تبدیل تاریخ میلادی به شمسی با خطا مواجه شد.";
    }
  }

  // WeekDayOrder
  get weekDayOrder(): number {
    let order = this.gregorianDate.getDay() + 1;
    if (order > 6) order -= 7;
    return order;
  }

  // IsHoliday
  get isHoliday(): boolean {
    return this.weekDayOrder === 6 || this.specialEvents.isHoliday;
  }

  // WeekDayName
  get weekDayName(): string {
    return PersianDate.WeekDayNames[this.weekDayOrder];
  }

  // MonthName
  get monthName(): string {
    return PersianDate.MonthNames[this.month - 1];
  }

  // Methods
  private setDefaults(): void {
    this._day = this._month = this._year = this._hour = this._minute = this._second = 0;
    this._errorMessage = "";
  }

  private copyFromPersianDate(persianDate: PersianDate): void {
    this._day = persianDate.day;
    this._month = persianDate.month;
    this._year = persianDate.year;
    this._hour = persianDate.hour;
    this._minute = persianDate.minute;
    this._second = persianDate.second;
    this._errorMessage = persianDate.errorMessage;
  }

  public static min(date1: PersianDate, date2: PersianDate): PersianDate {
    return date1.gregorianDate <= date2.gregorianDate ? date1 : date2;
  }

  public static max(date1: PersianDate, date2: PersianDate): PersianDate {
    return date1.gregorianDate >= date2.gregorianDate ? date1 : date2;
  }

  public addHours(hoursToAdd: number): PersianDate {
    return new PersianDate(new Date(this.gregorianDate.getTime() + hoursToAdd * 60 * 60 * 1000));
  }

  public addMinutes(minutesToAdd: number): PersianDate {
    return new PersianDate(new Date(this.gregorianDate.getTime() + minutesToAdd * 60 * 1000));
  }

  public addSeconds(secondsToAdd: number): PersianDate {
    return new PersianDate(new Date(this.gregorianDate.getTime() + secondsToAdd * 1000));
  }

  public addDays(daysToAdd: number): PersianDate {
    return new PersianDate(new Date(this.gregorianDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000));
  }

  public addMonths(monthsToAdd: number): PersianDate {
    const gregorian = new Date(this.gregorianDate);
    gregorian.setMonth(gregorian.getMonth() + monthsToAdd);
    return new PersianDate(gregorian);
  }

  public addYears(yearsToAdd: number): PersianDate {
    const gregorian = new Date(this.gregorianDate);
    gregorian.setFullYear(gregorian.getFullYear() + yearsToAdd);
    return new PersianDate(gregorian);
  }

  public toString(format: string = "yyyy/MM/dd"): string {
    const replacements: { [key: string]: string } = {
      "YYYY": this.year.toString().padStart(4, '0'),
      "yyyy": this.year.toString().padStart(4, '0'),
      "YY": (this.year % 100).toString().padStart(2, '0'),
      "yy": (this.year % 100).toString().padStart(2, '0'),
      "Y": (this.year % 1000).toString(),
      "y": (this.year % 1000).toString(),
      "MMMM": this.monthName,
      "MM": this.month.toString().padStart(2, '0'),
      "DD": this.weekDayName,
      "dddd": this.weekDayName,
      "dd": this.day.toString().padStart(2, '0'),
      "d": this.day.toString(),
      "HH": this.hour.toString().padStart(2, '0'),
      "hh": (this.hour % 12 === 0 ? 12 : this.hour % 12).toString().padStart(2, '0'),
      "mm": this.minute.toString().padStart(2, '0'),
      "ss": this.second.toString().padStart(2, '0'),
      "ev": this.specialEvents.title,
      "EV": this.specialEvents.title,
      "TD": this.isToday ? "امروز" : "",
      "PD": this.wasYesterday ? "دیروز" : ""
    };
    let result = format;
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replace(new RegExp(key, 'g'), value);
    }
    return PersianDate.toPersianDigits(result);
  }

  public toString_8(): string {
    const year = this.year < 1400 ? this.year - 1300 : this.year - 1400;
    return `${year.toString().padStart(2, '0')}/${this.month.toString().padStart(2, '0')}/${this.day.toString().padStart(2, '0')}`;
  }

  public toString_6(): string {
    const year = this.year < 1400 ? this.year - 1300 : this.year - 1400;
    return `${year.toString().padStart(2, '0')}${this.month.toString().padStart(2, '0')}${this.day.toString().padStart(2, '0')}`;
  }

  public toStringFullDateTime(): string {
    return `${this.toString()} ${this.hour.toString().padStart(2, '0')}:${this.minute.toString().padStart(2, '0')}:${this.second.toString().padStart(2, '0')}`;
  }

  public toStringTime(): string {
    return `${this.hour.toString().padStart(2, '0')}:${this.minute.toString().padStart(2, '0')}:${this.second.toString().padStart(2, '0')}`;
  }

  public toStringTimeSimple(): string {
    return `${this.hour.toString().padStart(2, '0')}:${this.minute.toString().padStart(2, '0')}`;
  }

  public toStringDateTime(): string {
    return `${this.toString()} ${this.hour.toString().padStart(2, '0')}:${this.minute.toString().padStart(2, '0')}`;
  }

  private checkPersianDate(): string {
    if (this.year < 0) return "سال خارج از محدوده است.";
    if (this.month < 1 || this.month > 12) return "ماه نامعتبر است.";
    if (this.day < 1 || this.day > this.monthLength(this.year, this.month)) return "روز نامعتبر است.";
    return "";
  }

  public static getDaysInMonth(year: number, month: number): number {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    return PersianDate.isLeapYear(year) ? 30 : 29;
  }

  private monthLength(year: number, month: number): number {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    return this.isLeapYear(year) ? 30 : 29;
  }

  public static toPersianDigits(input: string): string {
    if (!input) return input;
    return input.split('').map(c => {
      if (c >= '0' && c <= '9') return PersianDate.PersianDigits[parseInt(c)];
      return c;
    }).join('');
  }

  public toEnglishDigits(input: string): string {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    let result = input;
    for (let i = 0; i < persianDigits.length; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), i.toString());
    }
    return result;
  }

  // تبدیل تاریخ میلادی به شمسی (بر اساس MItoSH)
  private gregorianToJalali(gy: number, gm: number, gd: number): { year: number; month: number; day: number } {
    const mDay = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // آرایه ماه‌های میلادی
    if (gy % 4 === 0) mDay[2] = 29; // تنظیم سال کبیسه میلادی

    let eDays = gd;
    for (let i = 1; i < gm; i++) {
      eDays += mDay[i];
    }

    let iy: number, iDays: number, id: number, im: number;
    if (eDays < 80) {
      iy = gy - 622;
      iDays = eDays + 286;
      if (iy % 4 === 3) iDays += 1; // تنظیم سال کبیسه شمسی
    } else {
      iy = gy - 621;
      iDays = eDays - 79;
    }

    id = iDays;
    if (iDays <= 186) {
      im = 0;
      while (id > 31) {
        id -= 31;
        im++;
      }
    } else {
      id -= 186;
      im = 6;
      while (id > 30) {
        id -= 30;
        im++;
      }
    }
    im += 1;

    return { year: iy, month: im, day: id };
  }

  // تبدیل تاریخ شمسی به میلادی (بر اساس ShToMi)
  private jalaliToGregorian(jy: number, jm: number, jd: number, hour: number, minute: number, second: number): Date {
    try {
      // اعتبارسنجی تاریخ شمسی
      let validIriDate = true;
      if (jm < 7 && jd > 31) validIriDate = false;
      if (jm > 7 && jm < 12 && jd > 30) validIriDate = false;
      if (jm === 12) {
        if ((jy % 4 === 3 && jd > 30) || (jy % 4 !== 3 && jd > 29)) validIriDate = false;
      }
      if (jm > 12 || jm < 1) validIriDate = false;

      if (!validIriDate) {
        return new Date(1900, 0, 1); // تاریخ پیش‌فرض برای تاریخ نامعتبر
      }

      // محاسبه تعداد روزهای شمسی
      let iDays: number;
      if (jm <= 7) {
        iDays = 31 * (jm - 1) + jd;
      } else {
        iDays = 186 + (jm - 7) * 30 + jd;
      }

      // محاسبه سال و روزهای میلادی
      let ey: number, eDays: number;
      const ikbs = jy % 4;
      if (ikbs === 3 && iDays <= 287) {
        ey = jy + 621;
        eDays = iDays + 79;
      } else if (ikbs === 3 && iDays > 287) {
        ey = jy + 622;
        eDays = iDays - 287;
      } else if (ikbs !== 3 && iDays <= 286) {
        ey = jy + 621;
        eDays = iDays + 79;
      } else {
        ey = jy + 622;
        eDays = iDays - 286;
      }

      // تنظیم آرایه ماه‌های میلادی
      const mDay = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (ey % 4 === 0) mDay[2] = 29; // تنظیم سال کبیسه میلادی

      // محاسبه ماه و روز میلادی
      let em = 1;
      let ed = eDays;
      while (em < 13 && ed > mDay[em]) {
        ed -= mDay[em];
        em++;
      }

      return new Date(ey, em - 1, ed, hour, minute, second);
    } catch {
      return new Date(1900, 0, 1); // تاریخ پیش‌فرض در صورت خطا
    }
  }

  private persianDateToGregorian(year: number, month: number, day: number, hour: number, minute: number, second: number): Date {
    return this.jalaliToGregorian(year, month, day, hour, minute, second);
  }

  private static isLeapYear(year: number): boolean {
    return year % 4 === 3;
  }

  private isLeapYear(year: number): boolean {
    return PersianDate.isLeapYear(year);
  }
}

class PersianDayEvent {
  private static Events: { [key: string]: DayInfo } = {};
  private static isLoaded: boolean = false;

  public static async loadAsync(jsonPath: string = '/assets/PersianDayEvents.json'): Promise<void> {
    if (this.isLoaded) {
      //console.log("✅ مناسبت‌ها قبلاً بارگذاری شده‌اند.");
      return;
    }
    try {
      const response = await fetch(jsonPath);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const jsonData: { [key: string]: DayInfo } = await response.json();
      this.Events = jsonData;
      this.isLoaded = true;
      //  console.log("✅ مناسبت‌ها با موفقیت بارگذاری شدند.");
    } catch (ex: any) {
      console.error(`❌ خطا در بارگذاری مناسبت‌ها: ${ex.message}`);
      this.Events = {};
    }
  }

  public static getInfo(year: number, month: number, day: number): DayInfo {
    const persianDate = `${year.toString().padStart(4, '0')}/${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`;
    const info = this.Events[persianDate];
    return info || { date: persianDate, title: "", type: 0, isHoliday: false };
  }

  public static getEventForDate(persianDate: string): DayInfo | null {
    const info = this.Events[persianDate];
    return info || null;
  }
}

export { PersianDate, PersianDayEvent };
export type { DayInfo };